import { Fragment, type CSSProperties, type ReactNode } from "react";

import EmptyState, { EmptyRow } from "@buildpy/ui/empty-state";
import ListRowCard, { ListRows } from "@/components/list-card";
import SectionCard from "@buildpy/ui/section-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@buildpy/ui/table";
import { cn } from "@/lib/utils";
import {
  SnapshotData,
  SnapshotItem,
  SnapshotTrade,
  snapshotVatMode,
  SQM_PER_PYEONG,
} from "@buildpy/core/modules/quote/types";
import { ROUGH_TOTAL_LABEL } from "@buildpy/core/modules/quote/snapshot-vat";
import { CostBlock, CostSummary, vatSplitOf } from "@/modules/quote/ui/editor/totals-panel";
import Money from "@/components/money";
import { EMPTY_MARK, formatWon } from "@buildpy/core/lib/format";
import { formatQty, formatRate } from "@/modules/quote/ui/format";
import QuoteItemCard from "@/modules/quote/ui/item-card";
import { TRADE_GROUP_LABEL, TRADE_GROUPS } from "@buildpy/core/modules/template/trades";

interface Props {
  data: SnapshotData;
}

const cell = "px-2 py-1.5 text-sm";
const num = "px-2 py-1.5 text-right text-sm tabular-nums";
const head = "h-9 whitespace-nowrap px-2 text-xs";

// 0 이하는 빈 칸 표시(—) — 표 칸은 Money, 문자열 자리는 moneyText.
const money = (value: number) => <Money value={value > 0 ? value : null} />;
const moneyText = (value: number) => (value > 0 ? formatWon(value) : EMPTY_MARK);

const ItemRows = ({
  items,
  depth = 0,
}: {
  items: SnapshotItem[];
  depth?: number;
}) =>
  items.map((item, index) => {
    const isParent = item.children.length > 0;
    return (
      <Fragment key={`${depth}-${index}-${item.item}`}>
        <TableRow>
          <TableCell className={cn(cell, "text-muted-foreground")}>
            {item.scope || "—"}
          </TableCell>
          <TableCell
            className={cn(cell, isParent && "font-medium", depth > 0 && "pl-(--pl)")}
            style={
              depth > 0 ? ({ "--pl": `${8 + depth * 14}px` } as CSSProperties) : undefined
            }
          >
            {depth > 0 && <span className="text-muted-foreground">└ </span>}
            {item.item}
          </TableCell>
          <TableCell className={cn(cell, "whitespace-nowrap")}>
            {item.unit || "—"}
          </TableCell>
          <TableCell className={num}>
            {isParent ? "—" : formatQty(item.qty)}
          </TableCell>
          <TableCell className={num}>
            {isParent ? "—" : money(item.material_unit_price)}
          </TableCell>
          {/* SET 부모는 자기 단가를 안 쓴다 — 자재처럼 노무도 비운다. */}
          <TableCell className={num}>
            {isParent ? "—" : money(item.labor_unit_price)}
          </TableCell>
          <TableCell className={cn(num, "font-medium")}>
            {money(item.amount)}
          </TableCell>
          <TableCell className={cn(cell, "text-muted-foreground")}>
            {item.note}
          </TableCell>
        </TableRow>
        {isParent && <ItemRows depth={depth + 1} items={item.children} />}
      </Fragment>
    );
  });

// 금액 0원이고 조건·비고도 없는 공종 — 「고객 직접 구매」 같은 0원 조건이 있으면 접지 않는다.
export const isEmptyTrade = (trade: Pick<SnapshotTrade, "amount" | "condition_text">): boolean =>
  trade.amount === 0 && (trade.condition_text ?? "").trim() === "";

// 공종 하나의 항목 표 — 펼쳐 보는 카드.
const TradeItems = ({ trade }: { trade: SnapshotTrade }) => (
  <SectionCard as="details" padding="none">
    <summary className="flex cursor-pointer items-center justify-between gap-x-2 px-3 py-2 text-sm">
      <span className="font-medium">{trade.name}</span>
      <span className="tabular-nums text-muted-foreground">
        {trade.items.length}개 · <Money value={trade.amount} />
      </span>
    </summary>

    <div className="hidden border-t border-border md:block">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={head}>범위</TableHead>
            <TableHead className={head}>작업내역</TableHead>
            <TableHead className={head}>규격</TableHead>
            <TableHead className={cn(head, "text-right")}>수량</TableHead>
            <TableHead className={cn(head, "text-right")}>자재 단가</TableHead>
            <TableHead className={cn(head, "text-right")}>노무 단가</TableHead>
            <TableHead className={cn(head, "text-right")}>합계</TableHead>
            <TableHead className={head}>비고</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {trade.items.length === 0 ? (
            <EmptyRow colSpan={8}>항목이 없습니다.</EmptyRow>
          ) : (
            <ItemRows items={trade.items} />
          )}
        </TableBody>
      </Table>
    </div>
    {/* 폰(md 미만)은 8열 표 대신 항목 카드 — 고객 견적서 폰 뷰와 같은 카드. */}
    <div className="flex flex-col gap-y-2 border-t border-border p-2 md:hidden" data-testid="quote-item-cards">
      {trade.items.length === 0 ? (
        <EmptyState variant="plain">항목이 없습니다.</EmptyState>
      ) : (
        trade.items.map((item, index) => <QuoteItemCard key={`${index}-${item.item}`} item={item} testId="quote-item-card" />)
      )}
    </div>
  </SectionCard>
);

/**
 * 상세 화면 본문. 발송된 견적은 스냅샷이 들어오므로 여기서는 **보여 주기만** 한다 —
 * 금액을 다시 계산하지 않는다. 화면이 계산하면 고객이 본 숫자와 갈라진다.
 */
const QuoteBody = ({ data }: Props) => {
  const { quote } = data;
  const trades = [...data.trades].sort((a, b) => a.sort_order - b.sort_order);
  // 0원 공종은 접어 둔다 — 템플릿 공종이 다 깔려 있어 빈 줄이 화면 절반을 차지했다.
  const filled = trades.filter((t) => !isEmptyTrade(t));
  const empty = trades.filter(isEmptyTrade);

  return (
    <div className="flex flex-col gap-y-4">
      {/* ── 갑지 요약 ─────────────────────────────────────────── */}
      <SectionCard padding="sm">
        <h2 className="mb-2 text-sm font-semibold">갑지 요약</h2>
        <dl className="grid gap-x-4 gap-y-1.5 text-sm md:grid-cols-2">
          <div className="flex gap-x-2">
            <dt className="w-20 shrink-0 text-muted-foreground">발주처</dt>
            <dd className="min-w-0">{data.customer.name}</dd>
          </div>
          <div className="flex gap-x-2">
            <dt className="w-20 shrink-0 text-muted-foreground">현장</dt>
            <dd className="min-w-0">{data.customer.site_address || "—"}</dd>
          </div>
          <div className="flex gap-x-2">
            <dt className="w-20 shrink-0 text-muted-foreground">공사명</dt>
            <dd className="min-w-0">{quote.title}</dd>
          </div>
          <div className="flex gap-x-2">
            <dt className="w-20 shrink-0 text-muted-foreground">
              {/* 가견적은 도급금액이 아니다 — 계약의 근거가 되는 숫자가 아니다. */}
              {data.rough ? "개산금액" : "도급금액"}
            </dt>
            <dd className="min-w-0 font-semibold tabular-nums">
              <Money value={quote.total} />
            </dd>
          </div>
        </dl>

        <div className="mt-3 border-t border-border pt-3">
          <p className="mb-1 text-xs text-muted-foreground">견적조건</p>
          {quote.conditions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              등록된 조건이 없습니다.
            </p>
          ) : (
            <ol className="list-decimal space-y-0.5 pl-4 text-sm">
              {quote.conditions.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ol>
          )}
        </div>

        {quote.extra_notice && (
          <div className="mt-3 rounded-md bg-muted p-2.5">
            <p className="mb-1 text-xs text-muted-foreground">
              별도 부담금 안내
            </p>
            <p className="whitespace-pre-wrap text-sm">{quote.extra_notice}</p>
          </div>
        )}
      </SectionCard>

      {/*
        가견적은 **공종이 없는 것이 정상**이다. 아래 공종 표를 그대로 두면
        「공종이 없습니다」 빈 표가 서고, 합계 패널에도 없는 개념(스타일 공사
        소계·관리비 0%)이 줄줄이 뜬다 — 담당자가 뭔가 빠뜨린 줄 안다.
      */}
      {data.rough ? (
        <SectionCard padding="sm">
          <h2 className="mb-2 text-sm font-semibold">개산</h2>
          <dl className="flex flex-col gap-y-1.5 text-sm">
            <div className="flex items-baseline justify-between gap-x-3">
              <dt className="text-muted-foreground">면적</dt>
              <dd className="tabular-nums">
                {data.rough.area_pyeong}평{" "}
                <span className="text-muted-foreground">
                  (약 {(data.rough.area_pyeong * SQM_PER_PYEONG).toFixed(1)}㎡)
                </span>
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-x-3">
              <dt className="text-muted-foreground">평당 단가</dt>
              <dd className="tabular-nums">
                <Money value={data.rough.unit_price} />
              </dd>
            </div>
            {/* 공급가액·부가세·개산 금액은 옆 합계 패널에 한 번만 — 여기는 산출 근거만 둔다. */}
          </dl>
        </SectionCard>
      ) : (
        <>
          <section className="rounded-lg border border-border">
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className={head}>공종</TableHead>
                    <TableHead className={cn(head, "text-right")}>
                      자재비
                    </TableHead>
                    <TableHead className={cn(head, "text-right")}>
                      노무비
                    </TableHead>
                    <TableHead className={cn(head, "text-right")}>합계</TableHead>
                    <TableHead className={head}>조건</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trades.length === 0 && (
                    <EmptyRow colSpan={5}>공종이 없습니다.</EmptyRow>
                  )}
                  {trades.length > 0 && filled.length === 0 && (
                    <EmptyRow colSpan={5}>금액이 들어간 공종이 없습니다.</EmptyRow>
                  )}

                  {TRADE_GROUPS.map((group) => {
                    const rows = filled.filter((t) => t.group === group);
                    if (rows.length === 0) return null;
                    return (
                      <Fragment key={group}>
                        <TableRow variant="group">
                          <TableCell
                            className="px-2 py-1 text-xs font-semibold"
                            colSpan={5}
                          >
                            {TRADE_GROUP_LABEL[group]}
                          </TableCell>
                        </TableRow>
                        {rows.map((trade) => (
                          <TableRow key={`${trade.trade}-${trade.sort_order}`}>
                            <TableCell className={cn(cell, "whitespace-nowrap")}>
                              {trade.name}
                            </TableCell>
                            <TableCell className={num}>
                              {money(trade.material_amount)}
                            </TableCell>
                            <TableCell className={num}>
                              {money(trade.labor_amount)}
                            </TableCell>
                            <TableCell className={cn(num, "font-medium")}>
                              {money(trade.amount)}
                            </TableCell>
                            <TableCell
                              className={cn(cell, "text-muted-foreground")}
                            >
                              {trade.condition_text}
                            </TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {/* 폰(md 미만)은 공종 한 줄 = 이름·합계 · 자재·노무 · 조건 — 표는 합계 열이 밀렸다. */}
            <ListRows className="px-3 md:hidden" label="공종 요약" testId="quote-trade-rows">
              {(trades.length === 0 || filled.length === 0) && (
                <li>
                  <EmptyState variant="plain">{trades.length === 0 ? "공종이 없습니다." : "금액이 들어간 공종이 없습니다."}</EmptyState>
                </li>
              )}
              {TRADE_GROUPS.map((group) => {
                const rows = filled.filter((t) => t.group === group);
                if (rows.length === 0) return null;
                return (
                  <Fragment key={group}>
                    <li className="-mx-3 bg-muted px-3 py-1 text-xs font-semibold first:rounded-t-lg">{TRADE_GROUP_LABEL[group]}</li>
                    {rows.map((trade) => (
                      <ListRowCard
                        key={`${trade.trade}-${trade.sort_order}`}
                        amount={money(trade.amount)}
                        meta={[`자재 ${moneyText(trade.material_amount)}`, `노무 ${moneyText(trade.labor_amount)}`]}
                        subtitle={trade.condition_text || undefined}
                        title={trade.name}
                        variant="row"
                      />
                    ))}
                  </Fragment>
                );
              })}
            </ListRows>
            {empty.length > 0 && (
              <details className="border-t border-border px-3 py-2 text-sm" data-testid="quote-empty-trades">
                <summary className="cursor-pointer text-muted-foreground">
                  빈 공종 {empty.length}개 보기
                </summary>
                <p className="mt-1.5 text-muted-foreground">
                  {empty.map((t) => t.name).join(" · ")} — 금액 0원
                </p>
              </details>
            )}
          </section>

          {/* ── 항목 ───────────────────────────────────────── */}
          <section className="flex flex-col gap-y-2">
            <h2 className="text-sm font-semibold">항목</h2>
            {filled.map((trade) => (
              <TradeItems key={`${trade.trade}-${trade.sort_order}`} trade={trade} />
            ))}
            {empty.length > 0 && (
              <details className="flex flex-col gap-y-2" data-testid="quote-empty-trade-items">
                <summary className="cursor-pointer py-1 text-sm text-muted-foreground">
                  빈 공종 {empty.length}개 보기
                </summary>
                <div className="mt-2 flex flex-col gap-y-2">
                  {empty.map((trade) => (
                    <TradeItems key={`${trade.trade}-${trade.sort_order}`} trade={trade} />
                  ))}
                </div>
              </details>
            )}
          </section>
        </>
      )}
    </div>
  );
};

interface TotalsProps extends Props {
  // 원가·마진 보기가 켜졌고 원가 요약이 있을 때만 원가 묶음을 그린다(스냅샷엔 원가가 없다).
  showCost?: boolean;
  cost?: CostSummary | null;
}

/** 합계 패널. 상세 화면 우측(좁은 화면에서는 아래)에 붙는다. */
export const QuoteTotals = ({ data, showCost = false, cost = null }: TotalsProps) => {
  const { quote } = data;
  const vatMode = snapshotVatMode(data);
  const excl = vatMode === "EXCL";
  const { supply, vat } = vatSplitOf(quote);
  const vatLine = (label: string, value: number, small: boolean) => (
    <div key={label} className={cn("flex items-baseline justify-between gap-x-3", small && "text-xs")}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd><Money value={value} /></dd>
    </div>
  );
  /**
   * 가견적에는 공종 구분도 관리비도 없다 — 평당단가에 이미 다 들어가 있다.
   * 상세견적 줄을 그대로 두면 「스타일 공사 소계 0원 · 관리비 (0%) 0원」 이
   * 떠서, 담당자가 뭔가 덜 넣었다고 읽는다.
   */
  const rows: Array<{ label: string; value: ReactNode; strong?: boolean }> =
    data.rough
      ? [
          { label: "면적", value: `${data.rough.area_pyeong}평` },
          {
            label: "평당 단가",
            value: <Money value={data.rough.unit_price} />,
          },
        ]
      : [
          {
            label: "기본 공사 소계",
            value: <Money value={quote.subtotal_basic} />,
          },
          {
            label: "스타일 공사 소계",
            value: <Money value={quote.subtotal_style} />,
          },
          {
            label: `관리비 (${formatRate(quote.mgmt_fee_rate)})`,
            value: <Money value={quote.mgmt_fee} />,
          },
          {
            label: excl ? "총계 (부가세 별도)" : "총계",
            value: <Money value={quote.gross} />,
            strong: true,
          },
          {
            label: "단수 정리",
            // 깎은 게 없으면 「−0원」 이 아니라 「0원」 이다.
            value:
              quote.rounding_adjustment > 0
                ? `−${formatWon(quote.rounding_adjustment)}`
                : formatWon(0),
          },
        ];

  return (
    <SectionCard padding="sm">
      <h2 className="mb-2 text-sm font-semibold">합계</h2>
      <dl className="flex flex-col gap-y-1.5 text-sm">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-x-3"
          >
            <dt className="text-muted-foreground">{row.label}</dt>
            <dd className={cn("tabular-nums", row.strong && "font-semibold")}>
              {row.value}
            </dd>
          </div>
        ))}
        {excl && (
          <div className="flex flex-col gap-y-1.5 border-t border-border pt-1.5" data-testid="quote-totals-vat">
            {vatLine("공급가액", supply, false)}
            {vatLine("부가세 (10%)", vat, false)}
          </div>
        )}
        <div className="mt-1 flex items-baseline justify-between gap-x-3 border-t border-border pt-2">
          <dt className="font-semibold">
            {data.rough ? ROUGH_TOTAL_LABEL : "최종 견적금액"}
          </dt>
          <dd className="text-base font-bold tabular-nums">
            <Money value={quote.total} />
          </dd>
        </div>
      </dl>
      {/* 가견적은 라벨이 이미 「부가세 포함」 을 말한다 — 꼬리표는 확정 금액이 아니라는 것만. */}
      <p className="mt-2 text-xs text-muted-foreground">
        {data.rough ? "확정 금액이 아닙니다 (개산)" : "부가세 포함"}
      </p>
      {!data.rough && !excl && (
        <dl className="mt-1 flex flex-col gap-y-0.5" data-testid="quote-totals-vat">
          {vatLine("공급가액", supply, true)}
          {vatLine("부가세 (10%)", vat, true)}
        </dl>
      )}
      {showCost && cost && <CostBlock cost={cost} />}
    </SectionCard>
  );
};

export default QuoteBody;
